import type { CustomerSpd } from '@makemydeal/dr-dash-types';
import type { CoBuyer, CommonConsumer, IShopper } from '@makemydeal/dr-platform-types';
import { makeMaxLength, makeRegex, makeCustom } from '@makemydeal/dr-shared-ui-utils';
import type { ValidationRule } from '@makemydeal/dr-shared-ui-utils';
import { resolveCellPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';
import { VIEW_MODE } from '../CustomerCardEnums';

export type CustomerSpdData = Omit<CustomerSpd, 'commonConsumerId'>;
export type CardConsumer = Omit<IShopper | CoBuyer, 'contactPreferences'> & CoBuyer;
export type CustomerCardFormValues = Omit<IShopper | CoBuyer, 'contactPreferences'> & CustomerSpdData & CoBuyer;

export type FieldValidationRules = ValidationRule[];

export enum PhoneType {
    CELL_PHONE = 'cellPhone',
    WORK_PHONE = 'workPhone',
    HOME_PHONE = 'homePhone'
}

// Ensure 'Sr' and 'Jr' are always capitalized correctly,
// to maintain compatibility with upstream and downstream systems
export const transformSuffix = (suffix?: string) => {
    if (suffix?.toLowerCase() === 'sr') return 'SR';
    if (suffix?.toLowerCase() === 'jr') return 'JR';
    return suffix;
};

export const getFormValues = (
    customer: CardConsumer | undefined,
    customerSpdData: CustomerSpdData | undefined,
    enableCorrectPhoneHandling?: boolean
): CustomerCardFormValues => ({
    firstName: customer?.firstName || '',
    middleName: customer?.middleName,
    lastName: customer?.lastName || '',
    title: customer?.title,
    maritalStatus: customer?.maritalStatus,
    relationshipToBuyer: customer?.relationshipToBuyer,
    participantRole: customer?.participantRole,
    suffix: transformSuffix(customer?.suffix),
    phone: customer?.phone,
    ...(enableCorrectPhoneHandling && {
        cellPhone: resolveCellPhone({
            cellPhone: customer?.cellPhone,
            homePhone: customer?.homePhone,
            workPhone: customer?.workPhone,
            phone: customer?.phone
        })
    }),
    homePhone: customer?.homePhone,
    workPhone: customer?.workPhone,
    email: customer?.email || '',
    emailAlternative: customer?.emailAlternative,
    streetAddress: customer?.streetAddress,
    streetAddress2: customer?.streetAddress2,
    city: customer?.city,
    state: customer?.state,
    zip: customer?.zip || '',
    county: customer?.county,
    business: customer?.business,
    billingAddress: customer?.billingAddress,

    //SPD Fields
    driversLicenseNumber: customerSpdData?.driversLicenseNumber,
    driversLicenseState: customerSpdData?.driversLicenseState,
    driversLicenseExpirationDate: customerSpdData?.driversLicenseExpirationDate,
    dateOfBirth: customerSpdData?.dateOfBirth,
    socialSecurityNumber: customerSpdData?.socialSecurityNumber,
    bankAccountNumber: customerSpdData?.bankAccountNumber,

    //Housing and Employment Info
    housingInfo: customer?.housingInfo || {},
    previousAddress: customer?.previousAddress,
    yearsAtCurrentAddress: customer?.yearsAtCurrentAddress,
    monthsAtCurrentAddress: customer?.monthsAtCurrentAddress,
    yearsAtPreviousAddress: customer?.yearsAtPreviousAddress,
    monthsAtPreviousAddress: customer?.monthsAtPreviousAddress,
    financialInfo: customer?.financialInfo || {},
    currentEmployment: customer?.currentEmployment,
    previousEmployment: customer?.previousEmployment
});

/**
 * Determines whether any of the provided values contains data.
 * Primitive values count when they are not undefined, null, or an empty string.
 * Object values count when any of their own values is non-empty (one level deep).
 */
export const hasAnyFieldValue = (...values: unknown[]): boolean => {
    const isNonEmptyPrimitive = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

    return values.some((value) => {
        if (value !== null && typeof value === 'object') {
            return Object.values(value as Record<string, unknown>).some(isNonEmptyPrimitive);
        }
        return isNonEmptyPrimitive(value);
    });
};

export const extractNonSpdData = (formValues: CustomerCardFormValues) => {
    const {
        driversLicenseNumber: _driversLicenseNumber,
        driversLicenseState: _driversLicenseState,
        driversLicenseExpirationDate: _driversLicenseExpirationDate,
        dateOfBirth: _dateOfBirth,
        socialSecurityNumber: _socialSecurityNumber,
        bankAccountNumber: _bankAccountNumber,
        ...nonSpdValues
    } = formValues;

    return nonSpdValues;
};

// Shared validation configurations for address fields (contract validation schema)
export const addressFieldValidations: Record<string, FieldValidationRules> = {
    streetAddress: [
        makeMaxLength(100, 'Street Address must be under 100 characters'),
        makeRegex(/^[A-Za-z0-9 #\\\-/.',]*$/, "Use only letters, numbers, and # / \\ - . ' ,")
    ],
    streetAddress2: [
        makeMaxLength(100, 'Street Address 2 must be under 100 characters'),
        makeRegex(/^[A-Za-z0-9 #\\\-/.',:]*$/, "Use only letters, numbers, and # / \\ - . ' , :")
    ],
    city: [
        makeMaxLength(50, 'City must be under 50 characters'),
        makeRegex(/^[A-Za-z0-9 #\-/.']*$/, "Use only letters, numbers, and # / - . '")
    ],
    zip: [
        makeCustom('Format must be 12345', (value: any) => {
            const zipRegex = /^\d{5}(?:[-]\d{4})?$|^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
            return !value || (value.length <= 10 && zipRegex.test(value));
        })
    ],
    county: [
        makeMaxLength(50, 'County must be under 50 characters'),
        makeRegex(/^[A-Za-z0-9-'.\s]+$/, "Use only letters, numbers, and - . ' ,")
    ]
};

export const determinePhoneType = (customer: IShopper | CoBuyer): PhoneType => {
    if (!customer?.phone) {
        return PhoneType.CELL_PHONE;
    }
    const phoneTypes = [PhoneType.CELL_PHONE, PhoneType.WORK_PHONE, PhoneType.HOME_PHONE];
    const matchingProperty = phoneTypes.find((propName) => customer[propName] === customer.phone);

    return matchingProperty || PhoneType.CELL_PHONE;
};

export const mapFormValuesToCustomerPayload = (
    formValues: CustomerCardFormValues,
    consumerId: string,
    dealershipId: string,
    originalCustomerValues: IShopper | CoBuyer,
    enableCorrectPhoneHandling?: boolean
): CommonConsumer & { creditPullConsentGiven?: boolean } => {
    const phoneType = determinePhoneType(originalCustomerValues);
    const phones = enableCorrectPhoneHandling
        ? { cellPhone: formValues.cellPhone, workPhone: formValues.workPhone, homePhone: formValues.homePhone }
        : {
              cellPhone: phoneType === PhoneType.CELL_PHONE ? formValues.phone : originalCustomerValues.cellPhone,
              workPhone:
                  formValues.workPhone ??
                  (phoneType === PhoneType.WORK_PHONE ? formValues.phone : originalCustomerValues.workPhone),
              homePhone:
                  formValues.homePhone ?? (phoneType === PhoneType.HOME_PHONE ? formValues.phone : originalCustomerValues.homePhone)
          };
    return {
        dealershipId: dealershipId,
        consumerId: consumerId,
        dealerGroupId: '',
        business: formValues.business && {
            ...formValues.business,
            ...(formValues.bankAccountNumber != null && { bankAccountNumber: formValues.bankAccountNumber })
        },
        firstName: formValues.firstName ?? '',
        middleName: formValues.middleName,
        lastName: formValues.lastName ?? '',
        title: formValues.title,
        suffix: formValues.suffix,
        maritalStatus: formValues.maritalStatus,
        relationshipToBuyer: formValues.relationshipToBuyer,
        participantRole: formValues.participantRole,
        primaryEmail: formValues.email,
        alternateEmail: formValues.emailAlternative,
        ...phones,
        street1: formValues.streetAddress,
        street2: formValues.streetAddress2,
        city: formValues.city,
        stateOrProvinceAbbreviation: formValues.state,
        countyOrRegion: formValues.county,
        postalCode: formValues.zip,
        billingAddress: formValues.billingAddress,
        customerType: formValues.customerType,
        ...(formValues.creditPullConsentGiven !== undefined && {
            creditPullConsentGiven: formValues.creditPullConsentGiven
        }),

        // additional credit-app fields
        housingInfo: formValues.housingInfo,
        yearsAtCurrentAddress: formValues.yearsAtCurrentAddress,
        monthsAtCurrentAddress: formValues.monthsAtCurrentAddress,
        previousAddress: formValues.previousAddress,
        yearsAtPreviousAddress: formValues.yearsAtPreviousAddress,
        monthsAtPreviousAddress: formValues.monthsAtPreviousAddress,
        currentEmployment: formValues.currentEmployment,
        previousEmployment: formValues.previousEmployment,
        financialInfo: formValues.financialInfo
    };
};

export const mapFormValuesToCreateBusinessPayload = (formValues: CustomerCardFormValues, enableCorrectPhoneHandling?: boolean) => {
    return {
        businessName: formValues.business?.legalName || '',
        firstName: formValues.firstName || '',
        middleName: formValues.middleName,
        lastName: formValues.lastName || '',
        suffix: formValues.suffix,
        email: formValues.email || '',
        emailAlternative: formValues.emailAlternative,
        phone: enableCorrectPhoneHandling ? formValues.cellPhone : formValues.phone,
        homePhone: formValues.homePhone,
        workPhone: formValues.workPhone,
        address: {
            streetAddress: formValues.streetAddress,
            streetAddress2: formValues.streetAddress2,
            city: formValues.city,
            state: formValues.state,
            zip: formValues.zip,
            county: formValues.county
        },
        billingAddress: {
            streetAddress: formValues.billingAddress?.streetAddress,
            streetAddress2: formValues.billingAddress?.streetAddress2,
            city: formValues.billingAddress?.city,
            state: formValues.billingAddress?.state,
            zip: formValues.billingAddress?.zip,
            county: formValues.billingAddress?.county
        }
    };
};

export const mapFormValuesToAssociateBusinessPayload = (
    formValues: CustomerCardFormValues,
    enableCorrectPhoneHandling?: boolean
) => {
    return {
        businessEntityId: formValues.business?.businessEntityId || '',
        firstName: formValues.firstName || '',
        middleName: formValues.middleName,
        lastName: formValues.lastName || '',
        suffix: formValues.suffix,
        email: formValues.email || '',
        emailAlternative: formValues.emailAlternative,
        phone: enableCorrectPhoneHandling ? formValues.cellPhone : formValues.phone,
        homePhone: formValues.homePhone,
        workPhone: formValues.workPhone,
        address: {
            streetAddress: formValues.streetAddress,
            streetAddress2: formValues.streetAddress2,
            city: formValues.city,
            state: formValues.state,
            zip: formValues.zip,
            county: formValues.county
        }
    };
};

export const generateTestId = (field: string, viewMode: VIEW_MODE) => {
    return viewMode === VIEW_MODE.COLLAPSED ? `customer-form-${field}-summary` : `customer-form-${field}`;
};
